import { create } from "zustand";

/** DOM id for the tasks panel — pairs the header chip `aria-controls`. */
export const TASK_PROGRESS_PANEL_ID = "task-progress-panel";

/** Marks the header chip so outside-click dismiss does not fight `toggle()`. */
export const TASK_PROGRESS_TRIGGER_ATTR = "data-task-sidebar-trigger";

interface TaskSidebarState {
  open: boolean;
  /**
   * Click-pinned. A hover-opened panel collapses again when the pointer leaves;
   * a pinned one stays until the user closes it or clicks outside.
   */
  pinned: boolean;
  /** Viewer-local hides shared with the header chip so counts stay in sync. */
  dismissedIds: ReadonlySet<string>;
  toggle: () => void;
  /** Hover-open: unpinned, so the panel collapses again when the pointer leaves. */
  expand: () => void;
  /**
   * App-initiated open (a new task appeared). Pinned, so the chip — which now reads
   * "Collapse tasks panel" — closes it on the first click instead of pinning it.
   */
  reveal: () => void;
  collapse: () => void;
  dismissLocally: (correlationId: string) => void;
  restoreLocal: (correlationId: string) => void;
  retainPresent: (presentIds: ReadonlySet<string>) => void;
}

function withDismissed(
  current: ReadonlySet<string>,
  correlationId: string,
  present: boolean,
): ReadonlySet<string> {
  if (present === current.has(correlationId)) return current;
  const next = new Set(current);
  if (present) next.add(correlationId);
  else next.delete(correlationId);
  return next;
}

export const useTaskSidebarStore = create<TaskSidebarState>((set) => ({
  open: false,
  pinned: false,
  dismissedIds: new Set(),
  // A click on a hover-opened panel pins it instead of closing it — otherwise the
  // panel the user just reached for would vanish under their cursor.
  toggle: () =>
    set((s) =>
      s.open && !s.pinned ? { open: true, pinned: true } : { open: !s.open, pinned: !s.open },
    ),
  expand: () => set({ open: true }),
  reveal: () => set({ open: true, pinned: true }),
  collapse: () => set({ open: false, pinned: false }),
  dismissLocally: (correlationId) =>
    set((s) => ({ dismissedIds: withDismissed(s.dismissedIds, correlationId, true) })),
  restoreLocal: (correlationId) =>
    set((s) => ({ dismissedIds: withDismissed(s.dismissedIds, correlationId, false) })),
  retainPresent: (presentIds) =>
    set((s) => {
      const next = new Set([...s.dismissedIds].filter((id) => presentIds.has(id)));
      return next.size === s.dismissedIds.size ? s : { dismissedIds: next };
    }),
}));
